import { Flex } from 'antd';
import { useEffect, useRef, useState } from 'react';

import { getUserByMagicLink } from '@/api/user';
import { resolveNotificationMagicLinkRedeemResult } from '@/components/route/notificationMagicLinkRedeem';
import { useRouteServerErrorRegistration } from '@/config/routeServerError';
import { IS_ADMIN_KEY } from '@/constants/roles';
import { DASHBOARD, LOGIN } from '@/constants/routes';
import { useUrlState } from '@/hooks/useUrlState';
import ErrorPage from '@/pages/Error';
import { useAppDispatch } from '@/store/hooks';
import { login } from '@/store/userSlice';
import Loader from '@/UI/Loader';
import {
  removeLocalStorageItem,
  storeAccessToken,
  ADMIN_TOKEN_KEY,
} from '@/utils/localStorage';

import type { NotificationDeepLinkParams } from '@/utils/notificationDeepLink';

function readDeepLinkParams(search: string): NotificationDeepLinkParams {
  const params = new URLSearchParams(search);
  return {
    next: params.get('type'),
    user_id: params.get('user_id'),
    candidate_id: params.get('candidate_id'),
    job_id: params.get('job_id'),
    interview_id: params.get('interview_id'),
    feedback_id: params.get('feedback_id'),
    stage_name: params.get('stage_name'),
  };
}

// Email magic-link entry: always redeem the link, then MFA or deep-link.
const NotificationRoute = () => {
  const [error, setError] = useState(false);
  const [isBootstrapping, setIsBootstrapping] = useState(true);
  const redeemStartedRef = useRef(false);
  const dispatch = useAppDispatch();
  const { history } = useUrlState();

  useRouteServerErrorRegistration(setError);

  /* eslint-disable hireport-design/no-useeffect-api-fetch -- bootstrap: magic-link notification deep-link auth + routing */
  useEffect(() => {
    const params = new URLSearchParams(history.location.search);
    const magic_link = params.get('magic_link');
    const deepLinkParams = readDeepLinkParams(history.location.search);

    if (!magic_link) {
      history.push(DASHBOARD);
      setIsBootstrapping(false);
      return;
    }

    if (redeemStartedRef.current) {
      return;
    }
    redeemStartedRef.current = true;

    getUserByMagicLink(magic_link)
      .then((res) => {
        const result = resolveNotificationMagicLinkRedeemResult(
          res,
          deepLinkParams,
        );

        if (result.kind === 'mfa') {
          history.push(LOGIN, { magicLinkMfa: result.bootstrap });
          return;
        }

        storeAccessToken(result.accessToken);
        dispatch(
          login({ user: result.user, access_token: result.accessToken }),
        );
        history.push(result.nextPath);
      })
      .catch(() => {
        removeLocalStorageItem('access_token');
        removeLocalStorageItem(IS_ADMIN_KEY);
        removeLocalStorageItem(ADMIN_TOKEN_KEY);
        history.push(LOGIN);
      })
      .finally(() => setIsBootstrapping(false));
  }, [history, dispatch]);
  /* eslint-enable hireport-design/no-useeffect-api-fetch */

  return (
    <Loader spinning={isBootstrapping && !error}>
      {error ? <ErrorPage /> : <Flex />}
    </Loader>
  );
};

export default NotificationRoute;
